/**
 * Gallery Preview Store - Manages preview mode and zoom functionality
 */

export function createGalleryPreviewStore() {
  return {
    preview_mode: false,
    preview_transitioning: false,
    preview_ready: false,
    mobile_preview_active: false,
    show_rotation_prompt: false,

    zoom_active: false,
    zoomed_artwork_index: null,
    zoom_transitioning: false,
    panzoom_instance: null,
    zoom_container: null,

    exit_preview_button: null,

    elements_hidden: {
      header: false,
      footer: false,
      interactive_panel: false,
      variant_modal: false,
      filter_modal: false,
      carousel: false
    },

    high_res_preloading: false,
    high_res_ready: false,
    high_res_promise: null,
    high_res_artwork_signature: null,

    layout_centering_active: false,
    layout_center_offset: 0,
    layout_centering_frame: null,
    layout_resize_handler: null,
    layout_resize_observer: null,

    saved_states: {
      body_overflow: null,
      user_has_interacted: false
    },

    timings: {
      lock_interactions: 16,
      close_modals: 300,
      fade_ui: 400,
      show_exit_button: 200,
      enable_preview: 100,
      zoom_transition: 300
    },

    async enterPreviewMode() {
      if (this.preview_mode || this.preview_transitioning) return;

      this.preview_transitioning = true;

      try {
        this.saveCurrentStates();

        if (window.innerWidth <= 989) {
          this.mobile_preview_active = true;
        }

        if (this.isMobilePortrait()) {
          this.show_rotation_prompt = false;
        }

        // Begin preparing high resolution assets as early as possible
        this.preloadHighResImages('enter');

        await this.lockInteractions();

        const closePromise = this.closeActiveUI();
        const fadePromise = this.fadeOutUIElements({ waitForTransition: false });
        await closePromise;
        if (fadePromise?.catch) {
          fadePromise.catch(error => console.warn('Preview fade-out warning:', error));
        }

        this.preview_mode = true;
        this.preview_ready = true;

        const galleryBuilder = document.querySelector('.gallery-builder');
        if (galleryBuilder) {
          galleryBuilder.classList.add('gallery-builder--preview-mode');
        }

        if (window.innerWidth <= 989) {
          const galleryBuilder = document.querySelector('.gallery-builder');

          const applyLandscapeRotation = () => {
            const isPortrait = window.matchMedia('(orientation: portrait)').matches;

            if (isPortrait) {
              this.show_rotation_prompt = false;
              if (galleryBuilder && this.show_rotation_prompt) {
                galleryBuilder.setAttribute('data-force-landscape', 'true');
              }
            } else {
              // Landscape mode - re-initialize preview to ensure proper positioning
              this.show_rotation_prompt = false;

              if (galleryBuilder) {
                galleryBuilder.removeAttribute('data-force-landscape');
              }

              requestAnimationFrame(() => {
                const body = document.querySelector('.gallery-builder__body');
                if (body) {
                  body.style = '';
                  body.style.display = 'none';
                  void body.offsetHeight;
                  body.style.display = '';
                }

                // Force CSS recalculation
                if (galleryBuilder) {
                  galleryBuilder.classList.remove('gallery-builder--preview-mode');
                  void galleryBuilder.offsetHeight;
                  galleryBuilder.classList.add('gallery-builder--preview-mode');
                }

                if (this.startLayoutCentering) {
                  this.stopLayoutCentering();
                  setTimeout(() => {
                    this.startLayoutCentering();
                  }, 100);
                }
              });
            }
          };

          applyLandscapeRotation();

          let orientationTimeout;
          const orientationHandler = () => {
            clearTimeout(orientationTimeout);
            orientationTimeout = setTimeout(() => {
              applyLandscapeRotation();
            }, 300);
          };

          window.addEventListener('orientationchange', orientationHandler);
          window.addEventListener('resize', orientationHandler);

          this._orientationHandler = orientationHandler;
          this._orientationTimeout = orientationTimeout;
          this._applyLandscapeRotation = applyLandscapeRotation;
        }
        this.createExitPreviewButton();

        this.startLayoutCentering();

      } catch (error) {
        console.error('Error entering preview mode:', error);
        this.resetPreviewState();
      } finally {
        this.preview_transitioning = false;
      }
    },

    async exitPreviewMode() {
      if (!this.preview_mode || this.preview_transitioning) return;

      const uiStore = Alpine.store('galleryUI');
      const lastActiveIndexBeforePreview = uiStore?.last_active_artwork_index_before_preview ?? null;

      this.preview_transitioning = true;
      this.show_rotation_prompt = false;

      this.mobile_preview_active = false;

      if (this._orientationHandler) {
        window.removeEventListener('orientationchange', this._orientationHandler);
        window.removeEventListener('resize', this._orientationHandler);
        this._orientationHandler = null;
      }
      if (this._orientationTimeout) {
        clearTimeout(this._orientationTimeout);
        this._orientationTimeout = null;
      }

      const galleryBuilder = document.querySelector('.gallery-builder');
      if (galleryBuilder) {
        galleryBuilder.removeAttribute('data-force-landscape');
      }

      try {
        if (this.zoom_active) {
          await this.closeZoom();
        }

        const stopCenteringPromise = this.stopLayoutCentering();

        this.cleanupExitPreviewButton();

        this.preview_mode = false;
        this.preview_ready = false;

        const galleryBuilder = document.querySelector('.gallery-builder');
        if (galleryBuilder) {
          galleryBuilder.classList.remove('gallery-builder--preview-mode');
        }

        await Promise.all([this.fadeInUIElements(), stopCenteringPromise]);

        this.restoreSavedStates();

        this.unlockInteractions();

        if (uiStore) {
          try {
            if (lastActiveIndexBeforePreview !== null && typeof uiStore.setActiveArtwork === 'function') {
              await uiStore.setActiveArtwork(lastActiveIndexBeforePreview);
            }
          } catch (error) {
            console.warn('Preview: failed to restore active artwork after exiting preview', error);
          } finally {
            uiStore.last_active_artwork_index_before_preview = null;
          }
        }

      } catch (error) {
        console.error('Error exiting preview mode:', error);
      } finally {
        this.preview_transitioning = false;
      }
    },

    async openZoom(artworkIndex, imageData) {
      if (this.zoom_active || this.zoom_transitioning) return;

      this.zoom_transitioning = true;

      try {
        this.zoomed_artwork_index = artworkIndex;
        this.zoom_active = true;

        if (!this.zoom_container) {
          this.createZoomContainer();
        }

        const coreStore = Alpine.store('galleryCore');
        const allImagesData = [];

        if (coreStore && coreStore.selected_artworks) {
          coreStore.selected_artworks.forEach((artwork, index) => {
            if (!artwork) return;

            const artworkImageData = this.getArtworkImageData(artwork, coreStore);
            if (artworkImageData) {
              allImagesData.push(artworkImageData);
            }
          });
        }

        if (allImagesData.length === 0) {
          allImagesData.push(imageData);
        }

        await this.setZoomImage(allImagesData);

        this.zoom_container.classList.add('gallery-zoom-modal--active');

        this.lockBodyScroll();

        await this.wait(this.timings.zoom_transition);

      } catch (error) {
        console.error('Error opening zoom:', error);
        this.zoom_active = false;
        this.zoomed_artwork_index = null;
      } finally {
        this.zoom_transitioning = false;
      }
    },

    async closeZoom() {
      if (!this.zoom_active || this.zoom_transitioning) return;

      this.zoom_transitioning = true;

      try {
        if (this.panzoom_instance) {
          this.panzoom_instance.destroy();
          this.panzoom_instance = null;
        }

        if (this.zoom_container) {
          this.zoom_container.classList.remove('gallery-zoom-modal--active');
        }

        await this.wait(this.timings.zoom_transition);

        this.cleanupZoomContainer();

        this.unlockBodyScroll();

        this.zoom_active = false;
        this.zoomed_artwork_index = null;

      } catch (error) {
        console.error('Error closing zoom:', error);
      } finally {
        this.zoom_transitioning = false;
      }
    },

    isMobilePortrait() {
      return window.innerWidth <= 989 &&
             window.matchMedia('(orientation: portrait)').matches;
    },

    saveCurrentStates() {
      const uiStore = Alpine.store('galleryUI');

      this.saved_states = {
        body_overflow: document.body.style.overflow,
        user_has_interacted: uiStore.user_has_interacted
      };
    },

    restoreSavedStates() {
      const uiStore = Alpine.store('galleryUI');

      // Don't restore modals - they should stay closed
      if (this.saved_states.body_overflow) {
        document.body.style.overflow = this.saved_states.body_overflow;
      }

      // Restore user interaction state for prompt visibility
      if (!this.saved_states.user_has_interacted) {
        uiStore.user_has_interacted = false;
      }
    },

    async lockInteractions() {
      const uiStore = Alpine.store('galleryUI');

      uiStore.preview_button_disabled = true;

      uiStore.lockPanel();

      await this.wait(this.timings.lock_interactions);
    },

    unlockInteractions() {
      const uiStore = Alpine.store('galleryUI');

      uiStore.preview_button_disabled = false;

      uiStore.unlockPanel();
    },

    async closeActiveUI() {
      const uiStore = Alpine.store('galleryUI');
      const promises = [];

      if (uiStore.filter_modal_open) {
        uiStore.filter_modal_open = false;
        promises.push(this.wait(this.timings.close_modals));
      }

      if (uiStore.variant_modal_open) {
        uiStore.closeVariantModal();
        promises.push(this.wait(this.timings.close_modals));
      }

      if (uiStore.active_artwork_index !== null) {
        uiStore.last_active_artwork_index_before_preview = uiStore.active_artwork_index;
        uiStore.active_artwork_index = null;
        promises.push(this.wait(this.timings.close_modals));
      }

      if (uiStore.hover_artwork_index !== null) {
        uiStore.hover_artwork_index = null;
      }

      await Promise.all(promises);
    },

    async fadeOutUIElements({ waitForTransition = true } = {}) {
      this.elements_hidden = {
        header: true,
        footer: true,
        interactive_panel: true,
        variant_modal: true,
        filter_modal: true,
        carousel: true
      };

      if (waitForTransition) {
        await this.wait(this.timings.fade_ui);
      }
    },

    async fadeInUIElements() {
      this.elements_hidden = {
        header: false,
        footer: false,
        interactive_panel: false,
        variant_modal: false,
        filter_modal: false,
        carousel: false
      };

      await this.wait(this.timings.fade_ui);
    },

    _getHighResSignature() {
      if (typeof Alpine === 'undefined' || !Alpine.store) {
        return null;
      }

      const coreStore = Alpine.store('galleryCore');
      if (!coreStore) return null;

      const orientation = coreStore.orientation || 'horizontal';
      const ids = (coreStore.selected_artworks || [])
        .map(artwork => artwork?.id)
        .filter(Boolean)
        .sort()
        .join('|');

      return `${orientation}:${ids}`;
    },

    _collectHighResUrls(includeZoom = true) {
      if (typeof Alpine === 'undefined' || !Alpine.store) {
        return [];
      }

      const coreStore = Alpine.store('galleryCore');
      if (!coreStore || typeof coreStore.getArtworkUrls !== 'function') {
        return [];
      }

      const urls = new Set();
      const selected = coreStore.selected_artworks || [];

      selected.forEach(artwork => {
        if (!artwork) return;
        const imageUrls = coreStore.getArtworkUrls(artwork);
        if (!imageUrls) return;

        if (imageUrls.large) {
          urls.add(imageUrls.large);
        }

        if (includeZoom && imageUrls.zoom) {
          urls.add(imageUrls.zoom);
        }
      });

      return Array.from(urls);
    },

    /**
     * Preload high resolution imagery for preview mode
     */
    async preloadHighResImages(_trigger = 'manual') {
      if (typeof Alpine === 'undefined' || !Alpine.store) {
        return false;
      }

      const coreStore = Alpine.store('galleryCore');
      if (!coreStore || typeof coreStore.preloadImageBatch !== 'function') {
        return false;
      }

      const signature = this._getHighResSignature();
      if (signature && signature !== this.high_res_artwork_signature) {
        this.high_res_ready = false;
        this.high_res_artwork_signature = signature;
      }

      if (this.high_res_ready) {
        return true;
      }

      if (this.high_res_preloading && this.high_res_promise) {
        await this.high_res_promise;
        return this.high_res_ready;
      }

      const urls = this._collectHighResUrls(true);
      if (urls.length === 0) {
        this.high_res_ready = true;
        return true;
      }

      const orientationKey = 'preview';
      const pending = urls.filter(url => !coreStore.isImagePreloaded?.(url, orientationKey));

      if (pending.length === 0) {
        this.high_res_ready = true;
        return true;
      }

      this.high_res_preloading = true;

      const preloadPromise = coreStore.preloadImageBatch(pending, orientationKey)
        .then(() => {
          this.high_res_ready = true;
          return true;
        })
        .catch(error => {
          console.error('Error preloading preview images:', error);
          return false;
        })
        .finally(() => {
          this.high_res_preloading = false;
          this.high_res_promise = null;
        });

      this.high_res_promise = preloadPromise;
      await preloadPromise;

      return this.high_res_ready;
    },

    async ensureImagePreloaded(url) {
      if (!url || typeof Alpine === 'undefined' || !Alpine.store) {
        return false;
      }

      const coreStore = Alpine.store('galleryCore');
      if (!coreStore || typeof coreStore.preloadImageBatch !== 'function') {
        return false;
      }

      const orientationKey = 'preview';

      if (coreStore.isImagePreloaded?.(url, orientationKey)) {
        return true;
      }

      await coreStore.preloadImageBatch([url], orientationKey);
      return true;
    },

    startLayoutCentering() {
      if (this.layout_centering_active) return;
      if (typeof document === 'undefined') return;

      const root = document.querySelector('.gallery-builder');
      const layout = root?.querySelector('.gallery-layout');

      if (!root || !layout) {
        return;
      }

      this.layout_centering_active = true;
      root.classList.add('gallery-builder--preview-centered');
      root.style.setProperty('--preview-layout-offset', '0px');

      // Ensure transition styles are applied before animating
      layout.getBoundingClientRect();

      const schedule = () => this.scheduleLayoutCentering();

      if (typeof window !== 'undefined') {
        this.layout_resize_handler = () => schedule();
        window.addEventListener('resize', this.layout_resize_handler, { passive: true });
      }

      if (typeof ResizeObserver !== 'undefined') {
        this.layout_resize_observer = new ResizeObserver(() => schedule());
        this.layout_resize_observer.observe(layout);
      }

      // Initial pass: allow browser to register base transform before animating
      const raf = typeof window !== 'undefined' && window.requestAnimationFrame
        ? window.requestAnimationFrame
        : (cb) => setTimeout(cb, 16);

      raf(() => {
        // Force a layout read to commit the zero offset state
        layout.getBoundingClientRect();

        raf(() => {
          this.scheduleLayoutCentering();

          // Re-run after fade completes to account for header/footer transitions
          setTimeout(() => this.scheduleLayoutCentering(), this.timings.fade_ui);
        });
      });
    },

    scheduleLayoutCentering() {
      if (!this.layout_centering_active) return;
      if (typeof window === 'undefined') {
        this.applyLayoutCentering();
        return;
      }

      const raf = window.requestAnimationFrame || ((cb) => setTimeout(cb, 16));
      const caf = window.cancelAnimationFrame || clearTimeout;

      if (this.layout_centering_frame) {
        caf(this.layout_centering_frame);
      }

      this.layout_centering_frame = raf(() => {
        this.layout_centering_frame = null;
        this.applyLayoutCentering();
      });
    },

    applyLayoutCentering() {
      if (!this.layout_centering_active || typeof document === 'undefined') return;

      const root = document.querySelector('.gallery-builder');
      const layout = root?.querySelector('.gallery-layout');

      if (!root || !layout) {
        return;
      }

      const layoutRect = layout.getBoundingClientRect();
      const viewportHeight = (typeof window !== 'undefined' && window.innerHeight)
        ? window.innerHeight
        : (document.documentElement?.clientHeight || layoutRect.height);

      const current = parseFloat(getComputedStyle(root).getPropertyValue('--preview-layout-offset') || '0');
      const adjustedTop = layoutRect.top - current;

      const offset = this.calculateLayoutOffset(adjustedTop, layoutRect.height, viewportHeight);
      if (offset === null) {
        return;
      }

      if (Math.abs(current - offset) < 0.5) {
        this.layout_center_offset = offset;
        root.style.setProperty('--preview-layout-offset', `${offset}px`);
        return;
      }

      root.style.setProperty('--preview-layout-offset', `${offset}px`);
      this.layout_center_offset = offset;
    },

    calculateLayoutOffset(layoutTop, layoutHeight, viewportHeight) {
      if (!Number.isFinite(layoutTop) || !Number.isFinite(layoutHeight)) {
        return null;
      }

      const layoutCenter = layoutTop + (layoutHeight / 2);
      const viewportCenter = viewportHeight / 2;
      let offset = viewportCenter - layoutCenter;

      if (!Number.isFinite(offset)) {
        offset = 0;
      }

      return Math.round(offset * 100) / 100;
    },

    /**
     * Stop centering animation with a graceful return to default position
     */
    async stopLayoutCentering() {
      if (!this.layout_centering_active) {
        return;
      }

      this.layout_centering_active = false;

      if (this.layout_centering_frame) {
        const caf = typeof window !== 'undefined' && window.cancelAnimationFrame
          ? window.cancelAnimationFrame
          : clearTimeout;
        caf(this.layout_centering_frame);
        this.layout_centering_frame = null;
      }

      if (this.layout_resize_handler && typeof window !== 'undefined') {
        window.removeEventListener('resize', this.layout_resize_handler);
        this.layout_resize_handler = null;
      }

      if (this.layout_resize_observer) {
        this.layout_resize_observer.disconnect();
        this.layout_resize_observer = null;
      }

      const root = typeof document !== 'undefined' ? document.querySelector('.gallery-builder') : null;

      if (root) {
        root.style.setProperty('--preview-layout-offset', '0px');
      }

      await this.wait(this.timings.fade_ui);

      if (root) {
        root.classList.remove('gallery-builder--preview-centered');
        root.style.removeProperty('--preview-layout-offset');
      }

      this.layout_center_offset = 0;
    },

    forceStopLayoutCentering() {
      if (this.layout_centering_frame) {
        const caf = typeof window !== 'undefined' && window.cancelAnimationFrame
          ? window.cancelAnimationFrame
          : clearTimeout;
        caf(this.layout_centering_frame);
        this.layout_centering_frame = null;
      }

      if (this.layout_resize_handler && typeof window !== 'undefined') {
        window.removeEventListener('resize', this.layout_resize_handler);
        this.layout_resize_handler = null;
      }

      if (this.layout_resize_observer) {
        this.layout_resize_observer.disconnect();
        this.layout_resize_observer = null;
      }

      const root = typeof document !== 'undefined' ? document.querySelector('.gallery-builder') : null;
      if (root) {
        root.classList.remove('gallery-builder--preview-centered');
        root.style.removeProperty('--preview-layout-offset');
      }

      this.layout_center_offset = 0;
      this.layout_centering_active = false;
    },

    createZoomContainer() {
      if (this.zoom_container) return;

      this.zoom_container = document.createElement('div');
      this.zoom_container.className = 'gallery-zoom-modal';
      this.zoom_container.innerHTML = `
        <div class="gallery-zoom-modal__backdrop"></div>
        <div class="gallery-zoom-modal__container" id="galleryZoomContainer">
          <div class="gallery-zoom-modal__images-wrapper"></div>
        </div>
      `;

      const backdrop = this.zoom_container.querySelector('.gallery-zoom-modal__backdrop');
      if (backdrop) {
        backdrop.addEventListener('click', () => {
          this.closeZoom();
        });
      }

      document.body.appendChild(this.zoom_container);
    },

    async setZoomImage(imagesDataArray) {
      if (!this.zoom_container) return;

      const imagesWrapper = this.zoom_container.querySelector('.gallery-zoom-modal__images-wrapper');
      if (!imagesWrapper) return;

      imagesWrapper.innerHTML = '';

      if (!Array.isArray(imagesDataArray)) {
        imagesDataArray = [imagesDataArray];
      }

      const loadPromises = imagesDataArray.map((imageData, index) => {
        return new Promise((resolve) => {
          const imageItem = document.createElement('div');
          imageItem.className = 'gallery-zoom-modal__image-item';
          imageItem.setAttribute('data-artwork-index', index);

          const img = document.createElement('img');
          img.className = 'gallery-zoom-modal__image';
          img.alt = imageData.title || 'Artwork';

          let imageUrl = imageData.url || imageData.src || imageData;

          if (typeof imageUrl === 'string' && imageUrl.startsWith('//')) {
            imageUrl = 'https:' + imageUrl;
          }

          img.onload = () => resolve();
          img.onerror = (error) => {
            console.warn('Failed to load zoom image:', imageUrl, error);
            resolve();
          };

          img.src = imageUrl;

          imageItem.appendChild(img);
          imagesWrapper.appendChild(imageItem);
        });
      });

      await Promise.all(loadPromises);
    },

    cleanupZoomContainer() {
      if (this.zoom_container) {
        this.zoom_container.remove();
        this.zoom_container = null;
      }
    },

    // Context-aware: closes zoom if active, otherwise exits preview
    createExitPreviewButton() {
      if (this.exit_preview_button) return;

      this.exit_preview_button = document.createElement('button');
      this.exit_preview_button.className = 'gallery-builder__exit-preview';
      this.exit_preview_button.setAttribute('aria-label', 'Exit Preview');
      this.exit_preview_button.innerHTML = `
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12.6832 17L15.7714 17C16.2395 17 16.6883 16.8156 17.0193 16.4874C17.3502 16.1592 17.5361 15.7141 17.5361 15.25L17.5361 4.75C17.5361 4.28587 17.3502 3.84075 17.0193 3.51256C16.6883 3.18437 16.2395 3 15.7714 3L12.6832 3M12.4639 10L2.46387 10M2.46387 10L6.28483 14M2.46387 10L6.28483 6" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span>Exit Preview</span>
      `;

      this.exit_preview_button.addEventListener('click', async () => {
        if (this.zoom_active) {
          await this.closeZoom();
        } else {
          await this.exitPreviewMode();
        }
      });

      // Append to body (same stacking context as zoom modal) so z-index works correctly
      document.body.appendChild(this.exit_preview_button);
    },

    cleanupExitPreviewButton() {
      if (this.exit_preview_button) {
        this.exit_preview_button.remove();
        this.exit_preview_button = null;
      }
    },

    lockBodyScroll() {
      document.body.style.overflow = 'hidden';
    },

    unlockBodyScroll() {
      document.body.style.overflow = '';
    },

    resetPreviewState() {
      this.preview_mode = false;
      this.preview_transitioning = false;
      this.preview_ready = false;
      this.mobile_preview_active = false;
      this.zoom_active = false;
      this.zoom_transitioning = false;
      this.zoomed_artwork_index = null;
      this.high_res_preloading = false;
      this.high_res_promise = null;
      this.high_res_ready = false;
      this.high_res_artwork_signature = null;
      this.forceStopLayoutCentering();

      const galleryBuilder = document.querySelector('.gallery-builder');
      if (galleryBuilder) {
        galleryBuilder.classList.remove('gallery-builder--preview-mode');
      }
      this.cleanupExitPreviewButton();
      this.cleanupZoomContainer();
      this.unlockBodyScroll();
    },

    getArtworkImageData(artwork, coreStore) {
      if (!artwork) return null;

      const storedUrls = coreStore?.getArtworkUrls?.(artwork);

      let imageUrl = '';
      if (storedUrls?.zoom) {
        imageUrl = storedUrls.zoom;
      } else if (storedUrls?.large) {
        imageUrl = storedUrls.large;
      } else if (storedUrls?.display) {
        imageUrl = storedUrls.display;
      } else if (artwork.featured_image) {
        imageUrl = artwork.featured_image;
      } else if (artwork.image) {
        imageUrl = artwork.image;
      } else if (artwork.images && artwork.images.length > 0) {
        imageUrl = artwork.images[0].src || artwork.images[0];
      }

      if (imageUrl) {
        const [baseUrl] = imageUrl.split('?');
        imageUrl = baseUrl;

        if (imageUrl.startsWith('//')) {
          imageUrl = 'https:' + imageUrl;
        }
      }

      if (!imageUrl) return null;

      return {
        url: imageUrl,
        title: artwork.title || 'Untitled',
        artist: artwork.vendor || artwork.artist || 'Unknown Artist'
      };
    },

    wait(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    }
  };
}
